/**
 * Offline-aware leads hook for the telecaller workspace.
 *
 * Returns leads from the Dexie cache if still within TTL; otherwise fetches
 * from the API, populates the cache, and returns the result.
 * Falls back to stale cache when offline.
 *
 * Also exposes `submitDisposition` — an offline-safe version of
 * updateDisposition that:
 *   1. Optimistically updates the cached CallLog
 *   2. Enqueues the PATCH to the outbox
 *   3. Attempts an immediate flush if online
 */

import { useState, useEffect, useCallback } from 'react'
import { offlineDB } from './db'
import { readCache, upsertCached, populateCache } from './cache'
import { enqueueWrite, flushOutbox } from './outbox'
import api from './api'
import { debugLog } from './debugLog'
import { LEAD_CACHE_HORIZON_DAYS } from './offlineConfig'
import type { Lead, CallLog } from '../types'

// ── Network status helper ─────────────────────────────────────────────────────

function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true
}

// ── Read cache hook ───────────────────────────────────────────────────────────

export function useOfflineLeads() {
  const [leads, setLeads] = useState<Lead[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isOffline, setIsOffline] = useState(!isOnline())

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      const horizonMs = Date.now() + LEAD_CACHE_HORIZON_DAYS * 24 * 60 * 60 * 1000
      const horizon = new Date(horizonMs).toISOString().split('T')[0]

      const rows = await readCache(
        'leads',
        async () => {
          const res = await api.get('/leads/my-queue')
          return res.data.items as Lead[]
        },
        (table) =>
          (table as typeof offlineDB.leads)
            .where('insurance_due_date')
            .belowOrEqual(horizon)
            .toArray(),
      )

      setLeads(rows as Lead[])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    load()

    const onOnline = () => { setIsOffline(false); load() }
    const onOffline = () => setIsOffline(true)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [load])

  return { leads, isLoading, isOffline, reload: load }
}

// ── Write outbox hook ─────────────────────────────────────────────────────────

export function useOfflineDisposition() {
  const [isPending, setIsPending] = useState(false)

  const submitDisposition = useCallback(async (
    callLogId: number,
    leadId: number,
    data: {
      disposition: string
      remarks?: string
      callback_at?: string
      od_discount_offered?: number
    },
  ): Promise<void> => {
    setIsPending(true)
    try {
      try {
        // Optimistic cache update — reflect the new disposition immediately
        const existing = await offlineDB.callHistory.get(callLogId)
        if (existing) {
          await upsertCached('callHistory', { ...existing, ...data } as CallLog)
        }

        // Enqueue the PATCH with FIFO ordering for this lead
        await enqueueWrite({
          leadId,
          method: 'PATCH',
          path: `/calls/${callLogId}`,
          body: data,
        })
      } catch (localErr) {
        // The local queue is a convenience; never lose the save because of it.
        debugLog('Disposition', 'offline queue failed, saving directly', String(localErr))
        await api.patch(`/calls/${callLogId}`, data)
        return
      }

      // Try immediate flush if online; silently leave in outbox if not
      if (isOnline()) {
        await flushOutbox()
      }
    } finally {
      setIsPending(false)
    }
  }, [])

  return { submitDisposition, isPending }
}

// ── Populate cache on fresh API response ──────────────────────────────────────

/**
 * Call this after any successful API fetch of leads/callHistory to keep
 * the cache warm without a separate background request.
 */
export async function warmLeadsCache(leads: Lead[]): Promise<void> {
  await populateCache('leads', leads)
}

export async function warmCallHistoryCache(calls: CallLog[]): Promise<void> {
  await populateCache('callHistory', calls)
}
